import { useCallback, useEffect, useState } from 'react';
import { getPremiacaoTabelas, getPremiacaoTabelaSegmentos } from '@spark/services/premiacao';
import { notification } from '@spark/ui-components/utils/notification';
import { toApiDate } from '@spark/utils/formatters';
import { extractMultiSelectApiValues } from '../utils/premiacaoMultiSelectFilters';

const normalizeFilters = (filtros) => {
  const normalized = {};

  Object.entries(filtros).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (key === 'Ativo' && value === false) {
      return;
    }
    if (key === 'Segmento') {
      const segmentos = extractMultiSelectApiValues(value);
      if (segmentos.length > 0) {
        normalized.Segmentos = segmentos;
      }
      return;
    }
    normalized[key] = value instanceof Date ? toApiDate(value) : value;
  });

  return normalized;
};

export const usePremiacaoTabelaSelection = (show) => {
  const [tabelas, setTabelas] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(10);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const carregarSegmentos = useCallback(async () => {
    try {
      const lista = await getPremiacaoTabelaSegmentos();
      setSegmentos(Array.isArray(lista) ? lista : []);
    } catch (error) {
      console.error('Erro ao carregar segmentos de tabelas de premiação:', error);
      notification.error(
        error?.message || 'Não foi possível carregar os segmentos',
        'Erro ao carregar segmentos'
      );
      setSegmentos([]);
    }
  }, []);

  const carregarTabelas = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        PaginaAtual: paginaAtual,
        QuantidadePorPagina: quantidadePorPagina,
        ...normalizeFilters(filtros)
      };

      const response = await getPremiacaoTabelas(params);
      const data = response?.data || response || {};
      const registros = Array.isArray(data.registros) ? data.registros : [];

      setTabelas(registros);
      setTotalRegistros(data.totalRegistros || registros.length);
    } catch (error) {
      console.error('Erro ao carregar tabelas de premiação:', error);
      notification.error(
        error?.message || 'Não foi possível carregar as tabelas de premiação',
        'Erro ao carregar tabelas'
      );
      setTabelas([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  }, [filtros, paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    if (show) {
      carregarSegmentos();
    }
  }, [show, carregarSegmentos]);

  useEffect(() => {
    if (show) {
      carregarTabelas();
    }
  }, [show, carregarTabelas]);

  const handleFiltrosChange = useCallback((novosFiltros) => {
    setFiltros(novosFiltros);
    setPaginaAtual(1);
  }, []);

  const handlePaginationChange = useCallback((paginationState) => {
    if (paginationState.pageSize !== quantidadePorPagina) {
      setQuantidadePorPagina(paginationState.pageSize);
      setPaginaAtual(1);
    } else if (paginationState.pageIndex !== undefined) {
      setPaginaAtual(paginationState.pageIndex + 1);
    }
  }, [quantidadePorPagina]);

  const resetFiltros = useCallback(() => {
    setFiltros({});
    setPaginaAtual(1);
    setQuantidadePorPagina(10);
  }, []);

  const reload = useCallback(() => {
    carregarTabelas();
  }, [carregarTabelas]);

  return {
    tabelas,
    segmentos,
    loading,
    filtros,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    resetFiltros,
    reload
  };
};
